import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header/Header';
import SEO from '../components/SEO/SEO';
import { useLivePrices } from '../hooks/useLivePrices';
import { getCoinIconUrl } from '../utils/helpers';
import { API_BASE_URL } from '../utils/apiConfig';
import TradesPromo from '../components/TradesPromo/TradesPromo';
import './InvestmentCalculatorPage.css';

// Timeframe labels
const timeFrameLabels = {
  '1M': '1 month ago',
  '3M': '3 months ago',
  '6M': '6 months ago',
  '1Y': '1 year ago',
  '2Y': '2 years ago',
};

// Days mapping for API
const timeFrameDays = {
  '1M': 30,
  '3M': 90,
  '6M': 180,
  '1Y': 365,
  '2Y': 730,
};

function InvestmentCalculatorPage() {
  const { t } = useTranslation();
  const { coinSymbol } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialCoin = coinSymbol?.toUpperCase() || searchParams.get('coin')?.toUpperCase() || 'BTC';

  // State
  const [selectedCoin, setSelectedCoin] = useState(initialCoin);
  const [investmentAmount, setInvestmentAmount] = useState(1000);
  const [selectedTimeFrame, setSelectedTimeFrame] = useState('1Y');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [availableCoins, setAvailableCoins] = useState([]);
  const [historicalPrices, setHistoricalPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [altcoinData, setAltcoinData] = useState(null);
  const [bubblesData, setBubblesData] = useState([]);
  const [moversHistoricalPrices, setMoversHistoricalPrices] = useState({});
  const [biggestTrade, setBiggestTrade] = useState(null);

  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Live prices
  const { prices, formatPrice, connected } = useLivePrices();

  // Load available coins
  useEffect(() => {
    const loadCoins = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/available-coins?hours=24`);
        const data = await res.json();
        if (data.success && data.coins) {
          setAvailableCoins(data.coins);
        }
      } catch (err) {
        console.error('Failed to load coins:', err);
      }
    };
    loadCoins();
  }, []);

  // Load altcoin index data for 30D movers
  useEffect(() => {
    const loadAltcoinData = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/altcoin-index`);
        const data = await res.json();
        if (data.success) {
          setAltcoinData(data);
        }
      } catch (err) {
        console.error('Failed to load altcoin index:', err);
      }
    };
    loadAltcoinData();
  }, []);

  // Load bubbles data for other timeframes
  useEffect(() => {
    const loadBubblesData = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/bubbles`);
        const data = await res.json();
        if (data.success && data.data) {
          // Flatten sectors structure: data[].children[] -> flat array
          const stablecoins = ['USDT', 'USDC', 'USDS', 'DAI', 'TUSD', 'BUSD', 'FDUSD', 'BSC-USD'];
          const allCoins = data.data
            .flatMap(sector => sector.children || [])
            .filter(coin => coin.symbol && !stablecoins.includes(coin.symbol.toUpperCase()))
            .sort((a, b) => (b.marketCap || 0) - (a.marketCap || 0))
            .slice(0, 30);
          setBubblesData(allCoins);
        }
      } catch (err) {
        console.error('Failed to load bubbles data:', err);
      }
    };
    loadBubblesData();
  }, []);

  // Load historical prices for top coins (for market movers calculation)
  useEffect(() => {
    const loadMoversHistoricalPrices = async () => {
      if (bubblesData.length === 0) return;

      const topSymbols = bubblesData.slice(0, 20).map(c => c.symbol?.toUpperCase());
      const symbolsToLoad = topSymbols.filter(s => s && !moversHistoricalPrices[s]);

      if (symbolsToLoad.length === 0) return;

      // Load historical prices for each symbol
      const promises = symbolsToLoad.map(async (symbol) => {
        try {
          const res = await fetch(`${API_BASE_URL}/api/historical-prices/${symbol}`);
          const data = await res.json();
          if (data.success && data.prices) {
            return { symbol, prices: data.prices };
          }
        } catch (err) {
          console.error(`Failed to load historical prices for ${symbol}:`, err);
        }
        return null;
      });

      const results = await Promise.all(promises);
      const newPrices = {};
      results.forEach(r => {
        if (r) newPrices[r.symbol] = r.prices;
      });

      setMoversHistoricalPrices(prev => ({ ...prev, ...newPrices }));
    };

    loadMoversHistoricalPrices();
  }, [bubblesData]);

  // Load historical prices for selected coin
  useEffect(() => {
    const loadHistoricalPrices = async () => {
      if (!selectedCoin) return;

      // Check if already loaded
      if (historicalPrices[selectedCoin]) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/historical-prices/${selectedCoin}`);
        const data = await res.json();

        if (data.success && data.prices) {
          setHistoricalPrices(prev => ({
            ...prev,
            [selectedCoin]: data.prices
          }));
        } else if (data.loading) {
          // Retry after delay
          setTimeout(() => loadHistoricalPrices(), 5000);
          return;
        }
      } catch (err) {
        console.error('Failed to load historical prices:', err);
      }
      setLoading(false);
    };

    loadHistoricalPrices();
  }, [selectedCoin, historicalPrices]);

  // Load biggest trade for selected coin
  useEffect(() => {
    const loadBiggestTrade = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/recent-trades?hours=24&limit=500`);
        const data = await res.json();
        if (data.success && data.trades) {
          const coinTrades = data.trades.filter(t => {
            const base = t.symbol?.replace(/USDT$|USD$|BUSD$/, '').toUpperCase();
            return base === selectedCoin;
          });
          if (coinTrades.length > 0) {
            const biggest = coinTrades.reduce((max, t) => t.dollarSize > max.dollarSize ? t : max, coinTrades[0]);
            setBiggestTrade(biggest);
          } else {
            setBiggestTrade(null);
          }
        }
      } catch (err) {
        console.error('Failed to load biggest trade:', err);
      }
    };
    loadBiggestTrade();
  }, [selectedCoin]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  // Filter coins based on search
  const filteredCoins = useMemo(() => {
    if (!searchTerm) return availableCoins.slice(0, 50);
    const term = searchTerm.toUpperCase();
    return availableCoins
      .filter(coin => coin.includes(term))
      .slice(0, 20);
  }, [availableCoins, searchTerm]);

  // Calculate investment result
  const result = useMemo(() => {
    const currentPrice = prices[selectedCoin];
    const coinPrices = historicalPrices[selectedCoin];
    const historicalPrice = coinPrices?.[selectedTimeFrame];

    if (!currentPrice || !historicalPrice) {
      return { available: false };
    }

    const priceChange = ((currentPrice - historicalPrice) / historicalPrice) * 100;
    const calculatedValue = investmentAmount * (1 + priceChange / 100);
    const profitLoss = calculatedValue - investmentAmount;
    const priceDiff = currentPrice - historicalPrice;

    return {
      available: true,
      currentPrice,
      historicalPrice,
      priceChange,
      calculatedValue,
      profitLoss,
      priceDiff,
      isProfit: priceChange >= 0
    };
  }, [selectedCoin, selectedTimeFrame, investmentAmount, prices, historicalPrices]);

  // Calculate market movers based on selected timeframe
  const marketMovers = useMemo(() => {
    // For 1M, use altcoin index data directly
    if (selectedTimeFrame === '1M' && altcoinData) {
      return {
        gainers: (altcoinData.topGainers || []).slice(0, 3).map(coin => ({
          symbol: coin.symbol?.toUpperCase(),
          name: coin.name,
          image: coin.image,
          change: coin.change
        })),
        losers: (altcoinData.topLosers || []).slice(0, 3).map(coin => ({
          symbol: coin.symbol?.toUpperCase(),
          name: coin.name,
          image: coin.image,
          change: coin.change
        }))
      };
    }

    // For other timeframes, calculate from bubbles + historical prices
    if (bubblesData.length === 0 || Object.keys(moversHistoricalPrices).length === 0) {
      return { gainers: [], losers: [] };
    }

    const coinsWithChange = bubblesData
      .filter(coin => {
        const symbol = coin.symbol?.toUpperCase();
        const currentPrice = prices[symbol];
        const historicalPrice = moversHistoricalPrices[symbol]?.[selectedTimeFrame];
        return currentPrice && historicalPrice;
      })
      .map(coin => {
        const symbol = coin.symbol?.toUpperCase();
        const currentPrice = prices[symbol];
        const historicalPrice = moversHistoricalPrices[symbol]?.[selectedTimeFrame];
        const change = ((currentPrice - historicalPrice) / historicalPrice) * 100;

        return {
          symbol,
          name: coin.name,
          image: coin.image,
          change
        };
      })
      .sort((a, b) => b.change - a.change);

    return {
      gainers: coinsWithChange.slice(0, 3),
      losers: coinsWithChange.slice(-3).reverse()
    };
  }, [selectedTimeFrame, altcoinData, bubblesData, prices, moversHistoricalPrices]);

  const handleCoinSelect = useCallback((coin) => {
    setSelectedCoin(coin);
    setSearchOpen(false);
    setSearchTerm('');
    // Update URL without page reload
    navigate(`/calculator/${coin}`, { replace: true });
  }, [navigate]);

  const handleMoverClick = useCallback((symbol) => {
    if (symbol) {
      handleCoinSelect(symbol);
    }
  }, [handleCoinSelect]);

  const formatTradeVolume = (dollarSize) => {
    if (!dollarSize) return '—';
    if (dollarSize >= 1e9) return `$${(dollarSize / 1e9).toFixed(1)}B`;
    if (dollarSize >= 1e6) return `$${(dollarSize / 1e6).toFixed(1)}M`;
    if (dollarSize >= 1e3) return `$${(dollarSize / 1e3).toFixed(0)}K`;
    return `$${dollarSize.toFixed(0)}`;
  };

  const formatPriceDisplay = (price) => {
    if (!price) return '$0';
    if (price < 0.01) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    if (price < 100) return `$${price.toFixed(2)}`;
    return `$${price.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="investment-calculator-page">
      <SEO
        page={coinSymbol ? "calculatorCoin" : "calculator"}
        dynamicParams={{ coinSymbol: selectedCoin, coinName: selectedCoin }}
      />
      <Header isConnected={connected} isSubscribed={true} />

      <div className="calc-page-container">
        {/* Trades Promo CTA */}
        <TradesPromo />

        <div className="calc-card">
          {/* Two-panel layout */}
          <div className="calc-layout">
            {/* Left Panel - Inputs */}
            <div className="calc-panel-left">
              {/* Header */}
              <div className="calc-header">
                <div className="calc-header-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <h1>{t('calculator.title', 'Investment Calculator')}</h1>
              </div>

              {/* Select Asset Section */}
              <div className="calc-section">
                <label className="calc-section-label">{t('calculator.selectAsset', 'Select Asset')}</label>
                <div className="calc-search-section" ref={dropdownRef}>
                  <div className="calc-search-input" onClick={() => setSearchOpen(true)}>
                    <div className="calc-selected-coin">
                      <img src={getCoinIconUrl(selectedCoin)} alt={selectedCoin} />
                      <span className="calc-coin-name">{selectedCoin}</span>
                    </div>
                    <svg className="calc-chevron" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {/* Dropdown */}
                  {searchOpen && (
                    <div className="calc-search-dropdown">
                      <div className="calc-search-dropdown-header">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                          ref={searchInputRef}
                          type="text"
                          placeholder={t('calculator.searchCoin', 'Search coin...')}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <button className="calc-close-btn" onClick={() => setSearchOpen(false)}>
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      <div className="calc-coin-list">
                        {filteredCoins.map(coin => (
                          <div
                            key={coin}
                            className="calc-coin-item"
                            onClick={() => handleCoinSelect(coin)}
                          >
                            <img src={getCoinIconUrl(coin)} alt={coin} />
                            <div className="calc-coin-info">
                              <span className="calc-coin-symbol">{coin}</span>
                            </div>
                            {prices[coin] && (
                              <span className="calc-coin-item-price">{formatPriceDisplay(prices[coin])}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Investment Amount Section */}
              <div className="calc-section">
                <label className="calc-section-label">{t('calculator.investment', 'Investment Amount')}</label>
                <div className="calc-input-wrapper">
                  <span className="calc-dollar-sign">$</span>
                  <input
                    type="number"
                    className="calc-investment-input"
                    value={investmentAmount}
                    onChange={(e) => setInvestmentAmount(Number(e.target.value) || 0)}
                    min="0"
                  />
                </div>
              </div>

              {/* Time Frame Section */}
              <div className="calc-section">
                <label className="calc-section-label">{t('calculator.timeFrame', 'Time Frame')}</label>
                <div className="calc-timeframe-container">
                  <div className="calc-timeframe-buttons">
                    {['1M', '3M', '6M', '1Y', '2Y'].map(tf => {
                      const hasData = historicalPrices[selectedCoin]?.[tf];
                      return (
                        <button
                          key={tf}
                          className={`calc-time-btn ${selectedTimeFrame === tf ? 'active' : ''} ${!hasData ? 'disabled' : ''}`}
                          onClick={() => hasData && setSelectedTimeFrame(tf)}
                          disabled={!hasData}
                        >
                          {tf}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel - Results */}
            <div className="calc-panel-right">
              <label className="calc-section-label">{t('calculator.estimatedBalance', 'Estimated Balance')}</label>

              {/* Result */}
              <div className="calc-result-section">
                {loading ? (
                  <div className="calc-loading">
                    <div className="calc-spinner"></div>
                    <span>{t('common.loading', 'Loading...')}</span>
                  </div>
                ) : result.available ? (
                  <>
                    <div className={`calc-value ${result.isProfit ? 'profit' : 'loss'}`}>
                      ${result.calculatedValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </div>

                    {/* Meta chips */}
                    <div className="calc-meta-row">
                      <span className={`calc-meta-chip ${result.isProfit ? 'profit' : 'loss'}`}>
                        {result.isProfit ? '+' : ''}{result.priceChange.toFixed(1)}%
                      </span>
                      <span className={`calc-meta-text ${result.isProfit ? 'profit' : 'loss'}`}>
                        {result.isProfit ? '+' : ''}${Math.abs(result.profitLoss).toLocaleString('en-US', { maximumFractionDigits: 0 })}{' '}
                        {result.isProfit ? t('calculator.profit', 'profit') : t('calculator.loss', 'loss')}
                      </span>
                    </div>

                    {/* Price Comparison Cards */}
                    <div className="calc-price-boxes">
                      <div className="calc-price-box">
                        <div className="calc-price-box-header">
                          <span>{timeFrameLabels[selectedTimeFrame]}</span>
                        </div>
                        <div className="calc-price-value neutral">
                          {formatPriceDisplay(result.historicalPrice)}
                        </div>
                      </div>
                      <div className="calc-price-box current">
                        <div className="calc-price-box-header">
                          <span>{t('calculator.currentPrice', 'Current price')}</span>
                        </div>
                        <div className={`calc-price-value ${result.isProfit ? 'profit' : 'loss'}`}>
                          {formatPriceDisplay(result.currentPrice)}
                        </div>
                        {/* Price diff inside the current price card */}
                        <div className={`calc-price-diff-inline ${result.isProfit ? 'profit' : 'loss'}`}>
                          {result.isProfit ? '+' : '-'}{formatPriceDisplay(Math.abs(result.priceDiff))}
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="calc-unavailable">
                    <span>{t('coinPage.comingSoon', 'Coming soon...')}</span>
                  </div>
                )}
              </div>

              {/* Footer */}
              <p className="calc-powered-by">Powered by <span>TraderMap</span></p>
              <p className="calc-disclaimer">
                * {t('calculator.disclaimer', 'Past performance does not guarantee future results')}
              </p>
            </div>
          </div>
        </div>

        {/* Info Row: Coin Details | Latest News | Biggest Trade */}
        <div className="calc-info-row">
          <Link
            className="calc-info-box calc-info-box-detail"
            to={`/price/${selectedCoin}`}
          >
            <div className="calc-info-box-icon">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="calc-info-box-text">
              <span className="calc-info-box-label">Coin Details</span>
              <span className="calc-info-box-sub">{selectedCoin} Price & Stats</span>
            </div>
          </Link>

          <Link
            className="calc-info-box calc-info-box-news"
            to={`/insights/${selectedCoin.toLowerCase()}`}
          >
            <div className="calc-info-box-icon">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
            </div>
            <div className="calc-info-box-text">
              <span className="calc-info-box-label">Latest Analysis</span>
              <span className="calc-info-box-sub calc-info-box-sub-news">{selectedCoin} Insights</span>
            </div>
          </Link>

          <div className="calc-info-box calc-info-box-trade">
            <div className="calc-info-box-icon">
              <div className="calc-live-dot"></div>
            </div>
            <div className="calc-info-box-text">
              <span className="calc-info-box-label">Biggest Trade Today</span>
              <span className="calc-info-box-trade-val">
                {biggestTrade ? (
                  <>
                    {selectedCoin} {formatTradeVolume(biggestTrade.dollarSize)}{' '}
                    <span className={`calc-pill-side ${biggestTrade.side === 'buy' ? 'long' : 'short'}`}>
                      {biggestTrade.side === 'buy' ? 'LONG' : 'SHORT'}
                    </span>
                  </>
                ) : (
                  <span style={{ color: '#555' }}>No trades yet</span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Market Movers Section */}
        <div className="calc-movers-section">
          {/* Top Gainers */}
          <div className="calc-movers-card">
            <div className="calc-movers-header">
              <h3>{t('calculator.topGainers', 'Top Gainers')}</h3>
              <span className="calc-movers-timeframe">{selectedTimeFrame}</span>
            </div>
            <div className="calc-movers-list">
              {marketMovers.gainers.length > 0 ? (
                marketMovers.gainers.map((coin, index) => (
                  <div
                    key={coin.symbol || index}
                    className="calc-mover-item"
                    onClick={() => handleMoverClick(coin.symbol)}
                  >
                    <div className="calc-mover-info">
                      <img
                        src={coin.image || getCoinIconUrl(coin.symbol)}
                        alt={coin.symbol}
                        className="calc-mover-icon"
                      />
                      <span className="calc-mover-symbol">{coin.symbol}</span>
                    </div>
                    <span className="calc-mover-change positive">
                      +{coin.change?.toFixed(1)}%
                    </span>
                  </div>
                ))
              ) : (
                <div className="calc-movers-loading">{t('common.loading', 'Loading...')}</div>
              )}
            </div>
          </div>

          {/* Top Losers */}
          <div className="calc-movers-card">
            <div className="calc-movers-header">
              <h3>{t('calculator.topLosers', 'Top Losers')}</h3>
              <span className="calc-movers-timeframe">{selectedTimeFrame}</span>
            </div>
            <div className="calc-movers-list">
              {marketMovers.losers.length > 0 ? (
                marketMovers.losers.map((coin, index) => (
                  <div
                    key={coin.symbol || index}
                    className="calc-mover-item"
                    onClick={() => handleMoverClick(coin.symbol)}
                  >
                    <div className="calc-mover-info">
                      <img
                        src={coin.image || getCoinIconUrl(coin.symbol)}
                        alt={coin.symbol}
                        className="calc-mover-icon"
                      />
                      <span className="calc-mover-symbol">{coin.symbol}</span>
                    </div>
                    <span className="calc-mover-change negative">
                      {coin.change?.toFixed(1)}%
                    </span>
                  </div>
                ))
              ) : (
                <div className="calc-movers-loading">{t('common.loading', 'Loading...')}</div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default InvestmentCalculatorPage;
